import {
  AxiosResponseAndError,
  ResponseType,
} from "../types/axiosResponseType";
import { QUERY_KEYS } from "./querykeys";
import ApiRequest from "../ApiRequest";
import { requestURLs } from "@/Environment";
import { useQuery } from "@tanstack/react-query";
import {
  OrgStatusEnumValue,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import { TaskColorPaletteEnum } from "@backend/src/schemas/userSchema";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";
import { AssignedUsers } from "./useProjectQuery";
import { ProjectPhase, ProjectPriority, ProjectType, Workstream } from "@/components/project/CreateProjectNoPopupForm";

export type UserOrganisationType = {
  userOrganisationId: string;
  jobTitle?: string;
  role?: keyof typeof UserRoleEnumValue;
  taskColour?: TaskColorPaletteEnum;
  user: {
    userId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarImg?: string;
  };
};

export type OrganisationResponseType = ResponseType<{
  organisationId: string;
  organisationName: string;
  industry: string;
  status: keyof typeof OrgStatusEnumValue;
  country: string;
  nonWorkingDays: OrgListOfNonWorkingDaysEnum[];
  logo1?: string;
  logo2?: string;
  createdAt: Date;
  updatedAt: Date;
  tenantId: string;
  createdBy: string;
  userOrganisation: UserOrganisationType[];
}>;

export interface ProjectManagersProjects {
  isActive?: boolean;
  assignedUsers: AssignedUsers[];
  projectId: string;
  organisationId: string;
  projectName: string;
  projectDescription: string;
  startDate: string;
  estimatedEndDate: string;
  status: string;
  defaultView: string;
  timeTrack: any;
  budgetTrack: any;
  currency: string;
  overallTrack: string;
  estimatedBudget: string;
  actualCost: any;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  spi: number | null;
  forecastBudgetVariance?: number | null;
  forecastVariance?: number | null;
  reCalculateBudget?: number | string | null;
  progressionPercentage?: number | string | null;
  reCalculateEndDate?: string | null;
  projectActualStartDate?: string | null;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  projectCode?: string | null;
  sponsor?: string | null;
  clientDepartment?: string | null;
  projectPhase?: ProjectPhase | string | null;
  priority?: ProjectPriority | string | null;
  projectType?: ProjectType | string | null;
  workstream?: Workstream | null;
  tags?: string[] | null;
  ganttColor?: string | null;
  portfolio?: {
    portfolioId: string;
    portfolioName: string;
    assignedUsers: {
      portfolioRole: string;
      user: {
        userId: string;
        email: string;
        firstName?: string;
        lastName?: string;
        avatarImg?: string;
      };
    }[];
  };
  program?: {
    programId: string;
    programName: string;
    assignedUsers: {
      programRole: string;
      user: {
        userId: string;
        email: string;
        firstName?: string;
        lastName?: string;
        avatarImg?: string;
      };
    }[];
  };
}
export interface Project {
  projectId: string;
  organisationId: string;
  projectName: string;
  projectDescription: string;
  startDate: string;
  estimatedEndDate: string;
  status: string;
  defaultView: string;
  timeTrack: any;
  budgetTrack: any;
  currency: string;
  overallTrack: string;
  estimatedBudget: string;
  actualCost: any;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  ganttColor?: string | null;
}

export interface StatusChartData {
  labels: string[];
  data: number[];
}

export interface OverallSituationChartData {
  labels: string[];
  data: number[];
}

export interface SpiChartData {
  labels: string[];
  data: number[];
}
export interface CpiChartData {
  CPILabels: string[];
  CPIdata: number[];
}

export type maangerPortfolioDashboardResponseType = ResponseType<{
  projects: ProjectManagersProjects[];
  totalCount?: number;
  page?: number;
  totalPages?: number;
  statusChartData?: StatusChartData;
  overallSituationChartData?: OverallSituationChartData;
  spiData?: SpiChartData;
  portfolioPercentage?: number;
  portfolioPercentageData?: { actualProgress: number; expectedProgress: number };
}>;

export type managerDashboardPortfolioDataType = {
  projects: ProjectManagersProjects[];
  totalCount?: number;
  page?: number;
  totalPages?: number;
  statusChartData?: StatusChartData;
  overallSituationChartData?: OverallSituationChartData;
  spiData?: SpiChartData;
  portfolioPercentage?: number;
  portfolioPercentageData?: { actualProgress: number; expectedProgress: number };
  CPIDataResponse?: CpiChartData;
  capacityData?: {
    demand: number;
    capacity: number;
    utilization: number | null;
    overCapacity: number;
    periodStart: string;
    periodEnd: string;
  };
};

function useProjectManagerPortfolioDashboardQuery(data: {
  programId: string;
  portfolioId: string;
  page?: number;
  limit?: number;
  search?: string;
}, enabled = true) {
  return useQuery<
    AxiosResponseAndError<maangerPortfolioDashboardResponseType>["response"],
    AxiosResponseAndError<maangerPortfolioDashboardResponseType>["error"]
  >({
    queryKey: [QUERY_KEYS.portfolioDashboard, data.portfolioId, data.programId, data.page, data.limit, data.search],
    queryFn: async ({ signal }) => {
      const params: Record<string, string> = {
        programId: data.programId,
        portfolioId: data.portfolioId,
        page: String(data.page ?? 1),
        limit: String(data.limit ?? 10),
      };
      if (data.search) {
        params.search = data.search;
      }
      return await ApiRequest.get(
        `${requestURLs.managerPortfolioDashboardData}?${new URLSearchParams(
          params,
        ).toString()}`,
        { signal },
      );
    },
    enabled: enabled,
  });
}

export default useProjectManagerPortfolioDashboardQuery;
